import { useCallback, useEffect, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import noop from 'lodash/noop';

import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  GridLegacy,
  Typography,
} from '@sbv-clients/ui-shared';
import { getApiErrorMessage } from '@sbv-clients/util-misc';

import FacilityForm, {
  FacilityFormValues,
} from '../../components/facility-form';
import {
  useAdminFacilityIdGetWithMutation,
  useUpdateAdminFacility,
} from '../../services/admin';

export const EditFacilityPage = () => {
  const navigate = useNavigate();
  const params = useParams<{ facilityId: string }>();
  const facilityId = params.facilityId as string;
  const adminFacilityIdGetWithMutation = useAdminFacilityIdGetWithMutation();
  const updateFacility = useUpdateAdminFacility();

  const data = adminFacilityIdGetWithMutation?.data;
  const { isIdle, isLoading, isError, error } = adminFacilityIdGetWithMutation;
  const defaultValues = useMemo(() => {
    return {
      name: data?.name,
      desktopUserName: '',
      databaseName: data?.databaseName,
      databaseHost: data?.databaseHost,
      databaseUser: data?.databaseUser,
      databasePassword: data?.databasePassword,
      databaseEncrypt: data?.databaseEncrypt,
      licenseKey: data?.licenseKey,
      teamSupportID: data?.teamSupportID,
      timezone: data?.timezone,
      usingMobile: data?.usingMobile,
      usingSbvPro: data?.usingSbvPro,
      usingSaaSAuthentication: data?.usingSaaSAuthentication,
    } as FacilityFormValues;
  }, [
    data?.databaseEncrypt,
    data?.databaseHost,
    data?.databaseName,
    data?.databasePassword,
    data?.databaseUser,
    data?.licenseKey,
    data?.name,
    data?.teamSupportID,
    data?.timezone,
    data?.usingMobile,
    data?.usingSaaSAuthentication,
    data?.usingSbvPro,
  ]);

  const onSubmit = useCallback(
    async (formValues: FacilityFormValues) => {
      try {
        await updateFacility.mutateAsync({ ...formValues, facilityId });
        navigate('/facility');
        // eslint-disable-next-line no-empty
      } catch (e) {}
    },
    [updateFacility, facilityId, navigate]
  );

  useEffect(() => {
    if (!facilityId) {
      return;
    }
    adminFacilityIdGetWithMutation.mutateAsync(facilityId).catch(noop);
    // eslint-disable-next-line
  }, [facilityId]);

  if (!facilityId) {
    return <Alert severity="error">Facility ID is required</Alert>;
  }
  if (isIdle || isLoading) {
    return (
      <Box p={4} display="flex" justifyContent="center" alignItems="center">
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return <Alert severity="error">{getApiErrorMessage(error)}</Alert>;
  }

  return (
    <GridLegacy container justifyContent="center">
      <GridLegacy item xs={12} sm={6}>
        <Card sx={{ p: 8 }}>
          <Typography variant="h3" mb={8}>
            Edit Facility Information
          </Typography>
          <FacilityForm
            mode="update"
            actionTitle="Update"
            defaultValues={defaultValues}
            onSubmit={onSubmit}
          />
          <Box mt={4}>
            <Link to="/facility">
              <Button fullWidth variant="outlined">
                Cancel
              </Button>
            </Link>
          </Box>
        </Card>
      </GridLegacy>
    </GridLegacy>
  );
};
